import { Ban, CheckCircle2, Loader2, XCircle } from 'lucide-react'

import type { Run } from '@ankor-io/common/lang/backend/pipeline/types'
import type { ReportGenerationPipelineData } from '@ankor-io/common/types/reports/generated-report'
import {
  Badge,
  CopyToClipboard,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import { useReportGenerationJobRunsQuery } from '@/queries/organization'

import { BackHeader } from '../integration/integration-back-header'

const runStatusIcon = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="size-4 text-green-500" />
    case 'FAILED':
      return <XCircle className="size-4 text-red-500" />
    case 'IN_PROGRESS':
      return <Loader2 className="size-4 text-yellow-500 animate-spin" />
    default:
      return <Ban className="size-4 text-gray-400" />
  }
}

const formatDate = (iso: string) => {
  const d = new Date(iso)
  const now = new Date()
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  if (d.toDateString() === now.toDateString()) return `Today, ${time}`
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
}

const formatRunType = (type: string) => {
  return type
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
}

export const ReportGenerationRunsView = ({
  organizationId,
  jobId,
  onBack,
  onSelectRun,
}: {
  organizationId: string
  jobId: string
  onBack: () => void
  onSelectRun?: (runId: string) => void
}) => {
  const { data: runs, isPending, error } = useReportGenerationJobRunsQuery(organizationId, jobId)

  return (
    <>
      <BackHeader
        label="Report Generation — Runs"
        onBack={onBack}
      />

      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}

      {runs && runs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No runs found.</p>}

      {runs && runs.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Run</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Created At</TableHeader>
              <TableHeader>Completed At</TableHeader>
              <TableHeader>Action</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run: Run<ReportGenerationPipelineData>) => (
              <TableRow
                key={run.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => onSelectRun?.(run.id)}
              >
                <TableCell className="font-mono text-sm">
                  <Badge color="gray">{run.id}</Badge>
                </TableCell>
                <TableCell>{runStatusIcon(run.status)}</TableCell>
                <TableCell className="text-sm text-gray-700 dark:text-gray-300">{formatRunType(run.type)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">{formatDate(run.createdAt)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">
                  {run.completedAt ? formatDate(run.completedAt) : '—'}
                </TableCell>
                <TableCell>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CopyToClipboard
                      text={run.id}
                      tooltip="Copy Run ID"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
